import React, { useCallback,useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AppWrapper, Spinner } from 'components';
import AffiliateBusinessModal from 'components/AffiliateBusinessModal/AffiliateBusinessModal';
import AffiliateCouponsList from 'components/AffiliateCouponsList/AffiliateCouponsList';
import AffiliateCustomCoupon from 'components/AffiliateCustomCoupon/AffiliateCustomCoupon';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import Pagination from 'components/Pagination/Pagination';
import ProductsStatus from 'components/ProductsStatus/ProductsStatus';
import { AFFILIATE_TYPES,COUPON_EXPIRY_TYPES, COUPON_STATES, COUPON_TYPES } from 'coupon-common';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import { actions as impactActions } from 'coupon-common/src/modules/impact';

// ─── Debug logger ────────────────────────────────────────────────────────────
const DEBUG = false;
const log = (label, data) => {
  if (!DEBUG) return;
  const ts = new Date().toISOString();
  data !== undefined
    ? console.log(`[ImpactOfferFetcher][${ts}] ${label}`, data)
    : console.log(`[ImpactOfferFetcher][${ts}] ${label}`);
};

// ─── Discount parser (deals) ──────────────────────────────────────────────────
const parseDiscount = (deal) => {
  if (deal.discount_type === 'PERCENT' || deal.discount_percent) {
    const pct = parseFloat(deal.discount_percent);
    if (!isNaN(pct) && pct > 0)
      return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: pct, amount_off: null };
  }
  if (deal.discount_type === 'FIXED' || deal.discount_amount) {
    const amt = parseFloat(deal.discount_amount);
    if (!isNaN(amt) && amt > 0)
      return { type: COUPON_TYPES.AMOUNT_OFF, percentage_off: null, amount_off: amt };
  }
  if (deal.type === 'FREE_SHIPPING')
    return { type: COUPON_TYPES.FREE_SHIPPING, percentage_off: null, amount_off: null };
  if (deal.type === 'BOGO' || deal.bogo_buy_quantity > 0 || /bogo|buy\s+one\s+get\s+one/i.test(deal.description || ''))
    return { type: COUPON_TYPES.BOGO, percentage_off: null, amount_off: null };
  const text = (deal.description || deal.name || '').toLowerCase();
  const pctMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:%|percent)\s*off/);
  if (pctMatch)
    return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: parseFloat(pctMatch[1]), amount_off: null };
  const amtMatch = text.match(/(?:save\s+)?\$\s*(\d+(?:\.\d+)?)\s*off|(\d+(?:\.\d+)?)\s*\$\s*off/);
  if (amtMatch)
    return { type: COUPON_TYPES.AMOUNT_OFF, percentage_off: null, amount_off: parseFloat(amtMatch[1] || amtMatch[2]) };
  return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: null, amount_off: null };
};

// ─── Number coercion (handles "$142.49", "1,234.50", plain numbers, etc.) ─────
const toNumber = (val) => {
  if (val === null || val === undefined || val === '') return NaN;
  if (typeof val === 'number') return val;
  const cleaned = String(val).replace(/[^0-9.-]/g, '');
  return cleaned === '' ? NaN : parseFloat(cleaned);
};

// ─── Does this catalog item actually have a usable discount? ──────────────────
const hasCatalogDiscount = (item) => {
  const discountPct   = toNumber(item.discount_percent);
  const currentPrice  = toNumber(item.current_price);
  const originalPrice = toNumber(item.original_price);
  if (!isNaN(discountPct) && discountPct > 0) return true;
  if (!isNaN(currentPrice) && !isNaN(originalPrice) && originalPrice > currentPrice) return true;
  return false;
};

// ─── Catalog discount parser ──────────────────────────────────────────────────
const parseCatalogDiscount = (item) => {
  const discountPct   = toNumber(item.discount_percent);
  const currentPrice  = toNumber(item.current_price);
  const originalPrice = toNumber(item.original_price);

  if (!isNaN(discountPct) && discountPct > 0)
    return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: discountPct, amount_off: null };

  if (!isNaN(currentPrice) && !isNaN(originalPrice) && originalPrice > currentPrice) {
    return {
      type: COUPON_TYPES.AMOUNT_OFF,
      percentage_off: null,
      amount_off: +(originalPrice - currentPrice).toFixed(2),
    };
  }

  return { type: COUPON_TYPES.PERCENTAGE_OFF, percentage_off: null, amount_off: null };
};

// ─── Mode constants ───────────────────────────────────────────────────────────
const MODE_DEALS   = 'deals';
const MODE_CATALOG = 'catalog';

const ImpactOfferFetcher = () => {
  const dispatch = useDispatch();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const campaigns     = useSelector((state) => { const val = state.impact?.campaigns || []; return val; });
  const deals         = useSelector((state) => state.impact?.deals || []);
  const catalogs      = useSelector((state) => state.impact?.catalogs || []);
  const catalogItems  = useSelector((state) => state.impact?.catalogItems || []);
  const catalogHasMore = useSelector((state) => state.impact?.catalogHasMore || false);
  const catalogPage    = useSelector((state) => state.impact?.catalogPage || 1);
  const impactLoading  = useSelector((state) => state.impact?.loading || {});
  const affiliateBusinesses = useSelector((state) => state.affiliateBusiness?.data?.results || []);

  // ── Local state ────────────────────────────────────────────────────────────
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [selectedCampaign, setSelectedCampaign]     = useState(null);
  const [selectedCatalogId, setSelectedCatalogId]   = useState('');
  const [selectedCatalog, setSelectedCatalog]       = useState(null);
  const [activeMode, setActiveMode]                 = useState(MODE_DEALS);
  const [fetchRequested, setFetchRequested]         = useState(false);
  const [filteredItems, setFilteredItems]           = useState([]);
  const [mappedCoupons, setMappedCoupons]           = useState([]);
  const [couponImages, setCouponImages]             = useState({});
  const [currentPage, setCurrentPage]               = useState(1);
  const [firstItemIndex, setFirstItemIndex]         = useState(1);
  const [lastItemIndex, setLastItemIndex]           = useState(10);
  const postsPerPage = 10;
  const [showCustomCoupon, setShowCustomCoupon]     = useState(false);
  const [modalOpen, setModalOpen]                   = useState(false);
  const [modalBusiness, setModalBusiness]           = useState(null);
  const [modalDefaultType, setModalDefaultType]     = useState(null);

  // ── Mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    log('mount');
    dispatch(impactActions.getImpactCampaigns());
    dispatch(affiliateActions.getAffiliateBusinesses());
  }, [dispatch]);

  // ── Populate filteredItems when source data arrives ────────────────────────
  useEffect(() => {
    if (!fetchRequested) return;
    const source = activeMode === MODE_DEALS
      ? deals
      : catalogItems.filter(hasCatalogDiscount);
    log('source data updated', {
      mode: activeMode,
      count: source.length,
      rawCount: activeMode === MODE_CATALOG ? catalogItems.length : deals.length,
    });
    setFilteredItems(source);
    setCurrentPage(1);
  }, [deals, catalogItems, fetchRequested, activeMode]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const selectedBusinessInDb = affiliateBusinesses.find(
    (b) => String(b.affiliate_code) === String(selectedCampaignId)
  ) || null;

  const selectedCampaignInDb = selectedCampaign
    ? affiliateBusinesses.some((b) => String(b.affiliate_code) === String(selectedCampaign.campaign_id))
    : false;

useEffect(() => {
  if (!campaigns.length || !affiliateBusinesses.length) return;

  console.table(
    campaigns.map((c) => {
      const match = affiliateBusinesses.find(
        (b) => String(b.affiliate_code) === String(c.campaign_id)
      );
      return {
        name: c.name,
        campaign_id: JSON.stringify(c.campaign_id),
        id_type: typeof c.campaign_id,
        matched: !!match,
        matched_name: match?.name || '',
      };
    })
  );

  console.table(
    affiliateBusinesses.map((b) => ({
      name: b.name,
      affiliate_code: JSON.stringify(b.affiliate_code),
      code_type: typeof b.affiliate_code,
      affiliate_type: b.affiliate_type,
    }))
  );

  console.log('businesses loaded:', affiliateBusinesses.length);
}, [campaigns, affiliateBusinesses]);



  // ── Map deal → coupon ──────────────────────────────────────────────────────
  const mapDealToCoupon = useCallback((deal, index) => {
    const defaultCategory = selectedBusinessInDb?.categories?.[0] || null;
    const parsed  = parseDiscount(deal);
    const now     = new Date();
    const endDate = deal.end_date ? new Date(deal.end_date) : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      localId:               `impact-deal-${deal.id || deal.campaign_id}-${index}`,
      title:                 deal.name ? (deal.name.length > 35 ? deal.name.slice(0, 32) + '...' : deal.name) : '',
      description:           deal.description || '',
      coupon_code:           deal.promo_code || '',
      type:                  parsed.type,
      percentage_off:        parsed.percentage_off,
      amount_off:            parsed.amount_off,
      affiliate_link:        selectedCampaign?.tracking_link || '',
      link_url:              selectedCampaign?.tracking_link || '',
      published_at:          deal.start_date ? new Date(deal.start_date) : now,
      expiration_date:       endDate,
      expiry_type:           COUPON_EXPIRY_TYPES.DATE,
      state:                 COUPON_STATES.PUBLISHED,
      auto_accept:           true,
      is_affiliate_or_flyer: true,
      affiliate_type:        AFFILIATE_TYPES.impact,
      advertiser_id:         deal.campaign_id,
      advertiser_name:       selectedCampaign?.name || '',
      category:              defaultCategory,
      _impactDealType:       deal.type || '',
      _impactDealScope:      deal.scope || '',
      _productImage:         deal.product_image_url || '',
      _editableDiscount:     parsed,
      _rawDeal:              deal,
    };
  }, [selectedBusinessInDb, selectedCampaign]);

  // ── Map catalog item → coupon ──────────────────────────────────────────────
  const mapCatalogItemToCoupon = useCallback((item, index) => {
    const defaultCategory = selectedBusinessInDb?.categories?.[0] || null;
    const now    = new Date();
    const parsed = parseCatalogDiscount(item);

    return {
      localId:               `impact-catalog-${item.id}-${index}`,
      title:                 item.name ? (item.name.length > 35 ? item.name.slice(0, 32) + '...' : item.name) : '',
      description:           item.description || '',
      coupon_code:           '',
      price:                 item.original_price || '',
      regular_price:         item.original_price || '',
      discounted_price:      item.current_price || '',
      price_display:         item.price_display || '',
      currency:              item.currency || 'USD',
      type:                  parsed.type,
      percentage_off:        parsed.percentage_off,
      amount_off:            parsed.amount_off,
      affiliate_link:        item.url || '',
      link_url:              item.url || '',
      published_at:          item.launch_date ? new Date(item.launch_date) : now,
      expiration_date:       item.expiration_date ? new Date(item.expiration_date) : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000),
      expiry_type:           COUPON_EXPIRY_TYPES.DATE,
      state:                 COUPON_STATES.PUBLISHED,
      auto_accept:           true,
      is_affiliate_or_flyer: true,
      affiliate_type:        AFFILIATE_TYPES.impact,
      advertiser_id:         item.campaign_id,
      advertiser_name:       selectedCampaign?.name || '',
      category:              defaultCategory,
      _productImage:         item.image_url || '',
      _additionalImages:     item.additional_images || [],
      _stockAvailability:    item.stock_availability || '',
      _editableDiscount:     parsed,
      _rawItem:              item,
    };
  }, [selectedBusinessInDb, selectedCampaign]);

  // ── Pagination slice ───────────────────────────────────────────────────────
  useEffect(() => {
    const lastIdx  = currentPage * postsPerPage;
    const firstIdx = lastIdx - postsPerPage;
    setFirstItemIndex(firstIdx + 1);
    setLastItemIndex(lastIdx);
    const slice = filteredItems.slice(firstIdx, lastIdx).map((item, i) =>
      activeMode === MODE_DEALS ? mapDealToCoupon(item, i) : mapCatalogItemToCoupon(item, i)
    );
    log('pagination slice', { currentPage, mode: activeMode, sliceCount: slice.length });
    setMappedCoupons(slice);
  }, [currentPage, filteredItems, activeMode, mapDealToCoupon, mapCatalogItemToCoupon]);

  // ── Auto-seed product images ───────────────────────────────────────────────
  useEffect(() => {
    if (!mappedCoupons.length) return;
    const assignments = {};
    mappedCoupons.forEach((coupon) => {
      if (coupon._productImage) assignments[coupon.localId] = coupon._productImage;
    });
    if (Object.keys(assignments).length > 0)
      setCouponImages((prev) => ({ ...prev, ...assignments }));
  }, [mappedCoupons]);

  // ── Build business data for modal ─────────────────────────────────────────
  const buildBusinessData = (campaign) => ({
    name:                 campaign.name,
    logo:                 campaign.logo_url || '',
    cover_image:          '',
    business_web_url:     campaign.tracking_link || '',
    terms_and_conditions: '',
    affiliate_code:       campaign.campaign_id,
    affiliate_type:       AFFILIATE_TYPES.impact,
    is_affiliate:         true,
    is_flyer:             false,
    categories:           [],
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const resetResults = () => {
    setFetchRequested(false);
    setFilteredItems([]);
    setMappedCoupons([]);
    setCouponImages({});
    setShowCustomCoupon(false);
  };

  const handleCampaignChange = (e) => {
    const id = e.target.value;
    log('handleCampaignChange', id);
    setSelectedCampaignId(id);
    setSelectedCampaign(campaigns.find((c) => c.campaign_id === id) || null);
    setSelectedCatalogId('');
    setSelectedCatalog(null);
    resetResults();
    if (id) {
      // Fetch catalogs for this campaign immediately
      dispatch(impactActions.getImpactCatalogs({ campaign_id: id }));
    }
  };

  const handleCatalogChange = (e) => {
    const id = e.target.value;
    log('handleCatalogChange', id);
    setSelectedCatalogId(id);
    setSelectedCatalog(catalogs.find((c) => c.id === id) || null);
    resetResults();
  };

  const handleFetchDeals = () => {
    if (!selectedCampaignId) return;
    log('handleFetchDeals', { selectedCampaignId });
    setShowCustomCoupon(false);
    setActiveMode(MODE_DEALS);
    setFetchRequested(true);
    dispatch(impactActions.getImpactDeals({ campaign_id: selectedCampaignId }));
  };

  const handleFetchCatalog = () => {
    if (!selectedCatalogId) return;
    log('handleFetchCatalog', { selectedCatalogId });
    setShowCustomCoupon(false);
    setActiveMode(MODE_CATALOG);
    setFetchRequested(true);
    dispatch(impactActions.getImpactCatalogItems({ catalog_id: selectedCatalogId, page: 1, page_size: 50 }));
  };

  const handleLoadMore = () => {
    if (!selectedCatalogId || !catalogHasMore) return;
    log('handleLoadMore', { page: catalogPage + 1 });
    dispatch(impactActions.getImpactCatalogItems({ catalog_id: selectedCatalogId, page: catalogPage + 1, page_size: 50 }));
  };

  const handleRefresh = () => {
    if (!selectedCampaignId) return;
    log('handleRefresh', { activeMode });
    setShowCustomCoupon(false);
    setFetchRequested(true);
    if (activeMode === MODE_DEALS) {
      dispatch(impactActions.getImpactDeals({ campaign_id: selectedCampaignId }));
    } else if (selectedCatalogId) {
      dispatch(impactActions.getImpactCatalogItems({ catalog_id: selectedCatalogId, page: 1, page_size: 50 }));
    }
  };

  const handleReset = () => {
    if (showCustomCoupon) {
      setShowCustomCoupon(false);
      setFetchRequested(false);
    }
  };

  const handleImageChange = (localId, newImageUrl) => {
    setCouponImages((prev) => ({ ...prev, [localId]: newImageUrl }));
  };

  const handleAddNewBusiness  = () => { setModalBusiness(null); setModalDefaultType(AFFILIATE_TYPES.impact); setModalOpen(true); };
  const handleAddThisCampaign = () => { if (!selectedCampaign) return; setModalBusiness(buildBusinessData(selectedCampaign)); setModalDefaultType(AFFILIATE_TYPES.impact); setModalOpen(true); };
  const handleModalClose      = () => { setModalOpen(false); setModalBusiness(null); setModalDefaultType(null); dispatch(affiliateActions.getAffiliateBusinesses()); };
  const handleCustomCoupon    = () => { setFilteredItems([]); setMappedCoupons([]); setShowCustomCoupon(true); setFetchRequested(true); };

  // ── Derived ────────────────────────────────────────────────────────────────
  const couponsWithImages = mappedCoupons.map((coupon) => ({
    ...coupon,
    image_url: couponImages[coupon.localId]
      ? [{ image_url: couponImages[coupon.localId], sequence: 1 }]
      : [],
  }));

  const campaignSelected = !!selectedCampaignId;
  const catalogSelected  = !!selectedCatalogId;
  const dealsLoading     = impactLoading.deals || false;
  const catalogsLoading  = impactLoading.catalogs || false;
  const catalogLoading   = impactLoading.catalogItems || false;
  const activeLoading    = activeMode === MODE_DEALS ? dealsLoading : catalogLoading;
  const statusTitle      = activeMode === MODE_DEALS ? '⚡ Impact Deals' : `📦 ${selectedCatalog?.name || 'Catalog Items'}`;

  const totalPages  = Math.ceil(filteredItems.length / postsPerPage);
  const onLastPage  = currentPage >= totalPages;

  return (
    <AppWrapper showHeader showSideNav showHeadingRow headingText="⚡ Impact.com Offer Tool">
      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'Impact Import']} />

        {/* ── Controls bar ── */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-end g-3">

              {/* Campaign dropdown */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">Campaign</label>
                <select
                  className="form-select"
                  value={selectedCampaignId}
                  onChange={handleCampaignChange}
                  disabled={impactLoading.campaigns}
                >
                  <option value="">
                    {impactLoading.campaigns ? 'Loading...' : '— Choose a campaign —'}
                  </option>
                  {campaigns.map((c) => (
                    <option key={c.campaign_id} value={c.campaign_id}>
                      {c.name}
                      {affiliateBusinesses.some((b) => String(b.affiliate_code) === String(c.campaign_id)) ? ' ✓' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Catalog dropdown — shown once a campaign is selected */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Catalog
                  {selectedCatalog && (
                    <span className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>
                      {Number(selectedCatalog.num_items).toLocaleString()} items
                    </span>
                  )}
                </label>
                <select
                  className="form-select"
                  value={selectedCatalogId}
                  onChange={handleCatalogChange}
                  disabled={!campaignSelected || catalogsLoading}
                >
                  <option value="">
                    {catalogsLoading
                      ? 'Loading catalogs...'
                      : !campaignSelected
                      ? '— Select a campaign first —'
                      : catalogs.length === 0
                      ? '— No catalogs available —'
                      : '— Choose a catalog —'}
                  </option>
                  {catalogs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({Number(c.num_items).toLocaleString()} items)
                    </option>
                  ))}
                </select>
              </div>

              {/* Campaign meta + DB status */}
              {selectedCampaign && (
                <div className="col-md-1">
                  <small className="text-muted d-block">
                    <strong>Status:</strong> {selectedCampaign.status || '—'}
                  </small>
                  {selectedCampaignInDb ? (
                    <small className="text-success d-block mt-1">✓ In DB</small>
                  ) : (
                    <small className="text-warning d-block mt-1">⚠ Not in DB</small>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="col-md-3 text-end d-flex flex-column gap-2 align-items-end">
                <button className="btn btn-outline-secondary btn-sm w-100" onClick={handleAddNewBusiness}>
                  + Add New Business
                </button>

                {campaignSelected && !selectedCampaignInDb && (
                  <button className="btn btn-warning btn-sm w-100" onClick={handleAddThisCampaign}>
                    + Add This Campaign
                  </button>
                )}

                <div className="d-flex gap-2 w-100">
                  <button
                    className={`btn btn-sm flex-grow-1${activeMode === MODE_DEALS && fetchRequested ? ' btn-primary' : ' btn-outline-primary'}`}
                    onClick={handleFetchDeals}
                    disabled={!campaignSelected || dealsLoading}
                  >
                    {dealsLoading ? <Spinner small /> : '🏷️ Deals'}
                  </button>
                  <button
                    className={`btn btn-sm flex-grow-1${activeMode === MODE_CATALOG && fetchRequested ? ' btn-success' : ' btn-outline-success'}`}
                    onClick={handleFetchCatalog}
                    disabled={!catalogSelected || catalogLoading}
                    title={!catalogSelected ? 'Select a catalog first' : ''}
                  >
                    {catalogLoading ? <Spinner small /> : '📦 Catalog'}
                  </button>
                  {fetchRequested && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleRefresh} disabled={activeLoading}>
                      🔄
                    </button>
                  )}
                </div>

                {campaignSelected && selectedCampaignInDb && (
                  <button className="btn btn-outline-primary btn-sm w-100" onClick={handleCustomCoupon}>
                    Custom Coupon +
                  </button>
                )}
              </div>
            </div>

            {!campaignSelected && !impactLoading.campaigns && (
              <p className="text-muted mt-3 mb-0">
                ☝️ Select a campaign, then choose a catalog or click <strong>Deals</strong>.
              </p>
            )}
          </div>
        </div>

        {/* ── Results ── */}
        {fetchRequested && (
          <>
            {showCustomCoupon ? (
              <AffiliateCustomCoupon
                affiliateBusinesses={affiliateBusinesses}
                selectedBusinessId={selectedBusinessInDb ? selectedBusinessInDb.id : null}
                dispatch={dispatch}
                inputValue={String(selectedCampaignId)}
                onClose={handleReset}
              />
            ) : (
              <>
                <ProductsStatus
                  title={statusTitle}
                  items={filteredItems}
                  firstIndex={firstItemIndex}
                  lastIndex={lastItemIndex}
                  loading={activeLoading}
                  displayedItems={couponsWithImages}
                  productsRequested={fetchRequested}
                />

                <Pagination
                  totalPosts={filteredItems.length}
                  postsPerPage={postsPerPage}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />

                <AffiliateCouponsList
                  coupons={couponsWithImages}
                  loading={activeLoading}
                  affiliateBusinesses={affiliateBusinesses}
                  selectedBusinessId={selectedBusinessInDb ? selectedBusinessInDb.id : null}
                  inputValue={String(selectedCampaignId)}
                  dispatch={dispatch}
                  selectedBusiness={selectedBusinessInDb}
                  onImageChange={handleImageChange}
                  defaultCategory={selectedBusinessInDb?.categories?.[0] || null}
                  showDealFields
                />

                {/* Load more — catalog mode only */}
               {activeMode === MODE_CATALOG && catalogHasMore && onLastPage && (
                <div className="text-center mt-3 mb-4">
                    <button
                    className="btn btn-outline-success px-4"
                    onClick={handleLoadMore}
                    disabled={catalogLoading}
                    >
                    {catalogLoading ? <Spinner small /> : `Load More (page ${catalogPage + 1})`}
                    </button>
                </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <AffiliateBusinessModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        business={modalBusiness}
        defaultAffiliateType={modalDefaultType}
      />
    </AppWrapper>
  );
};

export default ImpactOfferFetcher;